import * as React from 'react';
import * as ScrollAreaPrimitive from '@radix-ui/react-scroll-area';

import { cn } from '@/lib/utils';

const ScrollArea = React.forwardRef<
	React.ElementRef<typeof ScrollAreaPrimitive.Root>,
	React.ComponentPropsWithoutRef<typeof ScrollAreaPrimitive.Root>
>(({ className, children, ...props }, ref) => {
	const [isScrolling, setIsScrolling] = React.useState(false);
	const scrollTimeoutRef = React.useRef<ReturnType<typeof setTimeout>>();
	const rafRef = React.useRef<number>();

	const handleScroll = React.useCallback(() => {
		if (rafRef.current) cancelAnimationFrame(rafRef.current);

		rafRef.current = requestAnimationFrame(() => {
			setIsScrolling(true);

			if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
			scrollTimeoutRef.current = setTimeout(() => setIsScrolling(false), 120);
		});
	}, []);

	return (
		<ScrollAreaPrimitive.Root
			ref={ref}
			className={cn('relative overflow-hidden', className)}
			{...props}
		>
			<ScrollAreaPrimitive.Viewport
				className="h-full w-full rounded-[inherit]"
				style={{ touchAction: 'pan-y' }}
				onScroll={handleScroll}
			>
				{children}
			</ScrollAreaPrimitive.Viewport>
			<ScrollBar isScrolling={isScrolling} />
			<ScrollAreaPrimitive.Corner />
		</ScrollAreaPrimitive.Root>
	);
});
ScrollArea.displayName = ScrollAreaPrimitive.Root.displayName;

const ScrollBar = React.memo(
	React.forwardRef<
		React.ElementRef<typeof ScrollAreaPrimitive.ScrollAreaScrollbar>,
		React.ComponentPropsWithoutRef<typeof ScrollAreaPrimitive.ScrollAreaScrollbar> & {
			isScrolling?: boolean;
		}
	>(({ className, orientation = 'vertical', isScrolling, ...props }, ref) => (
		<ScrollAreaPrimitive.ScrollAreaScrollbar
			ref={ref}
			orientation={orientation}
			className={cn(
				'flex touch-none select-none transition-colors',
				orientation === 'vertical' && 'h-full w-2.5 border-l border-l-transparent p-[1px]',
				orientation === 'horizontal' && 'h-2.5 flex-col border-t border-t-transparent p-[1px]',
				className
			)}
			{...props}
		>
			<ScrollAreaPrimitive.ScrollAreaThumb
				className={cn(
					'relative flex-1 rounded-full',
					isScrolling
						? 'bg-[hsl(var(--scrollbar-thumb))] transition-none'
						: 'bg-transparent transition-all duration-300'
				)}
			/>
		</ScrollAreaPrimitive.ScrollAreaScrollbar>
	))
);
ScrollBar.displayName = ScrollAreaPrimitive.ScrollAreaScrollbar.displayName;

export { ScrollArea, ScrollBar };
